import { createContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import useActiveUser from "../hooks/useActiveUser";

import Loader from "../components/Loader";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  const { data, isLoading, error: userFetchingError, mutate } = useActiveUser({ shouldFetch: !!token, token });
  const user = data?.user;

  useEffect(() => {
    if (!token) {
      const foundToken = localStorage.getItem("token");

      if (!foundToken) {
        setLoading(false);
      } else {
        setToken(foundToken);
      }
    }

    setIsInitialLoad(false);
  }, [token]);

  useEffect(() => {
    if (userFetchingError) {
      localStorage.removeItem("token");
      setToken(null);
    }

    setLoading(false);
  }, [userFetchingError]);

  useEffect(() => {
    if (isInitialLoad) return;

    const authRoutes = ['/login', '/forgot-password', '/reset-password', '/privacy-policy'];
    const sharedRoutes = ['/privacy-policy', '/scan'];

    const pageMappingByRole = {
      admin: {
        baseRoute: '/admin',
      },
      labourer: {
        baseRoute: '/l',
      },
      'partner-admin': {
        baseRoute: '/partner',
      },
      'partner-user': {
        baseRoute: '/partner-staff',
      }
    }

    if (!token && !isLoading && !user && !authRoutes.find(route => location.pathname.startsWith(route))) {
      setLoading(false);
      navigate(`/login?redirect_to=${encodeURIComponent(location.pathname + location.search)}`);
    }

    if (sharedRoutes.find(route => location.pathname.startsWith(route))) {
      return;
    }

    if (user) {
      const baseRoute = pageMappingByRole[user.role].baseRoute;

      if (!location.pathname.startsWith(baseRoute) && !sharedRoutes.find(route => location.pathname.startsWith(route))) {
        navigate(baseRoute);
      }
    }
  }, [user, location.pathname, navigate, isLoading, isInitialLoad, token]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      setToken,
      mutateUser: mutate
    }}>
      {isLoading && (
        <div className="fixed t-0 r-0 b-0 l-0 z-50 flex items-center justify-center bg-white w-screen h-screen">
          <Loader />
        </div>
      )}

      <div className="bg-slate-50 min-h-screen">
        {!isLoading && children}
      </div>
    </AuthContext.Provider>
  );
};
